import { useState } from "react";
import { CheckCircle2, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dataBR, dataHoraBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/ds";
import { ResponsiveModal } from "@/components/ds/ResponsiveModal";
import { NativeSelect } from "@/components/ds/NativeSelect";
import { dueTone } from "./noteUtils";

const Line = ({ label, children }) => (
  <div className="flex justify-between gap-3 text-sm">
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <span className="text-right">{children}</span>
  </div>
);

/** Detalhes, troca de situação, conclusão com resultado e histórico do recado. */
export function NoteDetailModal({ note, today, onClose, onEdit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [closing, setClosing] = useState(false);
  const [outcome, setOutcome] = useState("RESOLVIDO");
  const [outcomeNote, setOutcomeNote] = useState("");
  const done = note.status === "CONCLUIDO";
  const tone = dueTone(note, today);

  const run = async (fn, okMessage) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMessage);
      onChanged();
      if (okMessage.includes("concluído")) onClose();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={onClose}
      title={note.title}
      description={note.reference || "Recado do mural"}
      testid="note-detail-modal"
    >
      <div className="space-y-4">
        <p className="whitespace-pre-wrap rounded-xl border border-white/10 bg-secondary/40 p-4 text-sm"
           data-testid="note-detail-description">
          {note.description}
        </p>

        <div className="space-y-2 rounded-xl border border-white/10 bg-secondary/30 p-4">
          <Line label="Responsável">{note.assignee}</Line>
          <Line label="Prazo">
            <span className={tone.text} data-testid="note-detail-due">{dataBR(note.due_date)}</span>
          </Line>
          <Line label="Prioridade">
            {note.priority === "URGENTE" ? (
              <span className="text-red-400">Urgente</span>
            ) : (
              "Normal"
            )}
          </Line>
          <Line label="Situação">
            <span data-testid="note-detail-status">
              {done
                ? note.outcome === "RESOLVIDO"
                  ? "Concluído · resolvido"
                  : "Concluído · não resolvido"
                : note.status === "EM_ANDAMENTO"
                ? "Em andamento"
                : "Novo"}
            </span>
          </Line>
          <Line label="Criado por">
            {note.created_by_name || note.created_by} · {dataHoraBR(note.created_at)}
          </Line>
          {done && (
            <Line label="Concluído por">
              <span data-testid="note-detail-completed-by">
                {note.completed_by} · {dataHoraBR(note.completed_at)}
              </span>
            </Line>
          )}
        </div>

        {note.notes && (
          <div className="rounded-xl border border-white/10 bg-secondary/30 p-4">
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Observações</p>
            <p className="mt-1 whitespace-pre-wrap text-sm">{note.notes}</p>
          </div>
        )}

        {done && note.outcome_note && (
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4"
               data-testid="note-detail-outcome-note">
            <p className="text-[11px] uppercase tracking-wide text-emerald-300">
              Observação da conclusão
            </p>
            <p className="mt-1 whitespace-pre-wrap text-sm">{note.outcome_note}</p>
          </div>
        )}

        {!done && !closing && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => onEdit(note)} disabled={busy}
                    className="flex-1 gap-2 rounded-full" data-testid="note-edit-btn">
              <Pencil className="h-3.5 w-3.5" /> Editar
            </Button>
            {note.status === "NOVO" ? (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => run(() => api.put(`/notes/${note.id}/status`,
                                                 { status: "EM_ANDAMENTO" }),
                                   "Recado em andamento.")}
                className="flex-1 rounded-full"
                data-testid="note-start-btn"
              >
                Começar
              </Button>
            ) : (
              <Button
                variant="outline"
                disabled={busy}
                onClick={() => run(() => api.put(`/notes/${note.id}/status`, { status: "NOVO" }),
                                   "Recado voltou para novos.")}
                className="flex-1 rounded-full"
                data-testid="note-reopen-btn"
              >
                Voltar para novos
              </Button>
            )}
            <Button onClick={() => setClosing(true)} disabled={busy}
                    className="flex-1 gap-2 rounded-full" data-testid="note-complete-btn">
              <CheckCircle2 className="h-4 w-4" /> Concluir
            </Button>
          </div>
        )}

        {!done && closing && (
          <div className="space-y-3 rounded-xl border border-brand/30 bg-brand/5 p-4">
            <Field label="Resultado *">
              <NativeSelect
                value={outcome}
                onChange={setOutcome}
                options={[
                  { value: "RESOLVIDO", label: "Resolvido" },
                  { value: "NAO_RESOLVIDO", label: "Não resolvido" },
                ]}
                data-testid="note-outcome-select"
              />
            </Field>
            <Field
              label={outcome === "NAO_RESOLVIDO" ? "Motivo *" : "Observação da conclusão"}
              hint={outcome === "NAO_RESOLVIDO" ? "Obrigatório quando não resolvido" : "Opcional"}
            >
              <Textarea
                value={outcomeNote}
                onChange={(e) => setOutcomeNote(e.target.value)}
                rows={2}
                placeholder="Cliente desistiu do orçamento"
                data-testid="note-outcome-note-input"
              />
            </Field>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setClosing(false)} disabled={busy}
                      className="flex-1 rounded-full" data-testid="note-complete-cancel-btn">
                Voltar
              </Button>
              <Button
                disabled={busy}
                onClick={() => run(() => api.post(`/notes/${note.id}/complete`,
                                                  { outcome, outcome_note: outcomeNote }),
                                   "Recado concluído.")}
                className="flex-1 rounded-full"
                data-testid="note-complete-confirm-btn"
              >
                {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Confirmar conclusão
              </Button>
            </div>
          </div>
        )}

        <div className="rounded-xl border border-white/10 bg-secondary/30 p-4" data-testid="note-history">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Histórico</p>
          <ul className="mt-2 space-y-2">
            {(note.history || []).map((h, i) => (
              <li key={i} className="flex justify-between gap-3 text-xs">
                <span>
                  <span className="text-muted-foreground">{dataHoraBR(h.at)} · </span>
                  {h.note || h.status}
                </span>
                <span className="shrink-0 text-muted-foreground/70">{h.by}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </ResponsiveModal>
  );
}
